import { useEffect, useState } from "react";
import { getRequest, reviewRequest, getAssignments, getModels, manualAssign } from "../api";

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: 14 }}>
      <span style={{ width: 160, fontWeight: 600, flexShrink: 0, color: "var(--muted)" }}>{label}</span>
      <span>{value ?? "—"}</span>
    </div>
  );
}

function ApiKeyCard({ apiKey }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ background: "#f0fff4", border: "1px solid #9ae6b4", borderRadius: 8, padding: 20 }}>
      <div style={{ fontWeight: 700, color: "var(--success)", marginBottom: 8, fontSize: 14 }}>🔑 API Key</div>
      <div style={{
        fontFamily: "monospace", fontSize: 13,
        background: "#fff", padding: "10px 14px", borderRadius: 6,
        border: "1px solid #c6f6d5", wordBreak: "break-all",
        color: revealed ? "var(--text)" : "transparent",
        textShadow: revealed ? "none" : "0 0 8px rgba(0,0,0,0.5)",
        userSelect: revealed ? "text" : "none",
        filter: revealed ? "none" : "blur(4px)",
        transition: "filter 0.2s",
      }}>
        {apiKey}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button className="btn btn-outline btn-sm" onClick={() => setRevealed((v) => !v)}>
          {revealed ? "🙈 Hide" : "👁 Reveal"}
        </button>
        {revealed && (
          <button className="btn btn-outline btn-sm" onClick={copy}>
            {copied ? "✅ Copied!" : "📋 Copy"}
          </button>
        )}
      </div>
      <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
        Keep this key secure. It was also sent to the requester's email.
      </p>
    </div>
  );
}

// ── Risk notes renderer ────────────────────────────────────────────────────────
const RISK_LEVEL_COLORS = {
  low:      { bg: "#f0fff4", border: "#9ae6b4", text: "#276749", badge: "#38a169" },
  medium:   { bg: "#fffbeb", border: "#fcd34d", text: "#92400e", badge: "#d97706" },
  high:     { bg: "#fff5f5", border: "#feb2b2", text: "#c53030", badge: "#e53e3e" },
  critical: { bg: "#fff5f5", border: "#fc8181", text: "#9b2c2c", badge: "#9b2c2c" },
};

function RiskNotes({ notes, riskLevel }) {
  if (!notes) return null;
  const bullets = notes.split(" | ").map((n) => n.trim()).filter(Boolean);
  const velocityBullets = bullets.filter((b) => b.startsWith("[VELOCITY]"));
  const otherBullets    = bullets.filter((b) => !b.startsWith("[VELOCITY]"));
  const colors = RISK_LEVEL_COLORS[riskLevel] || RISK_LEVEL_COLORS.low;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, color: "var(--muted)",
                    textTransform: "uppercase", letterSpacing: "0.04em" }}>
        Risk Factors
      </div>

      {/* Velocity warning — top-priority banner */}
      {velocityBullets.map((b, i) => {
        const text = b.replace("[VELOCITY]", "").trim();
        return (
          <div key={i} style={{
            background: "#fff7ed", border: "1px solid #f97316",
            borderLeft: "4px solid #f97316",
            borderRadius: 6, padding: "10px 14px", marginBottom: 10,
            display: "flex", alignItems: "flex-start", gap: 10,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>⚡</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#c2410c",
                            textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
                High-Velocity Alert — requires extra scrutiny
              </div>
              <div style={{ fontSize: 13, color: "#7c2d12", lineHeight: 1.55 }}>{text}</div>
            </div>
          </div>
        );
      })}

      {/* Other risk factors */}
      {otherBullets.length > 0 && (
        <div style={{ background: colors.bg, border: `1px solid ${colors.border}`,
                      borderRadius: 6, padding: "10px 14px" }}>
          <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
            {otherBullets.map((b, i) => (
              <li key={i} style={{ fontSize: 13, color: colors.text, lineHeight: 1.55 }}>{b}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function RequestDetail({ requestId, onBack, auth = null }) {
  const [req, setReq] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState({
    status: "approved",
    reviewer: "",
    verification_notes: "",
    model_id: "",
  });
  const [manualModel, setManualModel] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, a, m] = await Promise.all([
        getRequest(requestId),
        getAssignments(requestId),
        getModels(true),
      ]);
      setReq(r);
      setAssignments(a);
      setModels(m);
      if (m.length) {
        // Pre-select the requester's preferred model if set, else first model
        const preferred = m.find((x) => x.id === r.preferred_model_id);
        setReview((rv) => ({ ...rv, model_id: preferred ? String(preferred.id) : String(m[0].id) }));
        setManualModel(String(m[0].id));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [requestId]); // eslint-disable-line

  const handleReview = async (e) => {
    e.preventDefault();
    setError("");
    setReviewing(true);
    try {
      await reviewRequest(requestId, {
        status: review.status,
        reviewer: review.reviewer || auth?.email || "",
        reviewer_name: auth?.name || "",
        reviewer_role: auth?.role || "",
        verification_notes: review.verification_notes,
        model_id: review.model_id ? Number(review.model_id) : null,
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setReviewing(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await manualAssign(requestId, {
        request_id: requestId,
        model_id: Number(manualModel),
        assigned_by: auth?.email || "admin",
        assignment_reason: manualReason,
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="empty">Loading…</div>;
  if (!req) return <div className="empty">Request not found.</div>;

  const isAdmin   = auth?.role === "admin";
  const isManager = auth?.role === "manager";
  // Managers may only review requests where they are the named manager
  const canApprove = isAdmin || (isManager && req.manager_email?.toLowerCase() === auth?.email?.toLowerCase());
  const canReview  = canApprove && (req.status === "verified" || req.status === "pending");
  const activeAssignment = assignments.find((a) => a.is_active);

  return (
    <>
      <div className="page-header">
        <button className="btn btn-outline btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>← Back to list</button>
        <h1>{req.title}</h1>
        <p>Request #{req.id} · submitted {fmt(req.created_at)}</p>
      </div>
      <div className="page-body">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 20, alignItems: "start" }}>

          {/* ── Left: details ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div className="card">
              <div className="card-header">Request Details</div>
              <div className="card-body">
                <InfoRow label="Requester" value={req.requester} />
                <InfoRow label="Requester Email" value={
                  <a href={`mailto:${req.requester_email}`} style={{ color: "var(--primary)" }}>{req.requester_email}</a>
                } />
                <InfoRow label="Manager Email" value={
                  <a href={`mailto:${req.manager_email}`} style={{ color: "var(--primary)" }}>{req.manager_email}</a>
                } />
                <InfoRow label="Department" value={req.department} />
                <InfoRow label="Domain" value={<span style={{ textTransform: "capitalize" }}>{req.domain}</span>} />
                <InfoRow label="Involves PII" value={req.involves_pii ? "Yes" : "No"} />
                <InfoRow label="Expected Users" value={req.expected_users?.toLocaleString()} />
                {req.preferred_model_id && (
                  <InfoRow label="Requested Model" value={
                    <span>{models.find((m) => m.id === req.preferred_model_id)?.name ?? `Model #${req.preferred_model_id}`}</span>
                  } />
                )}
                {req.approved_model_id && (
                  <InfoRow label="Approved Model" value={
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <strong style={{ color: "var(--success, #16a34a)" }}>
                        {models.find((m) => m.id === req.approved_model_id)?.name ?? req.approved_model?.name ?? `Model #${req.approved_model_id}`}
                      </strong>
                      {req.preferred_model_id && req.preferred_model_id !== req.approved_model_id && (
                        <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>
                          Assigned by approver (overridden)
                        </span>
                      )}
                    </div>
                  } />
                )}
                <InfoRow label="Status" value={<span className={`badge badge-${req.status}`}>{req.status}</span>} />
                <InfoRow label="Risk Level" value={<span className={`badge badge-${req.risk_level}`}>{req.risk_level}</span>} />
                <InfoRow label="Risk Score" value={<strong style={{ fontSize: 16 }}>{req.risk_score} / 10</strong>} />
                {(req.approver_name || req.reviewer) && (
                  <InfoRow label={req.status === "approved" ? "Approved By" : "Reviewer"} value={
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span>{req.approver_name || req.reviewer}</span>
                      {req.approver_role && (
                        <span className="badge" style={{
                          background: req.approver_role === "admin" ? "#ede9fe" : "#e0f2fe",
                          color: req.approver_role === "admin" ? "#6b21a8" : "#0369a1",
                          textTransform: "capitalize",
                          fontSize: 11,
                          padding: "2px 8px"
                        }}>
                          {req.approver_role}
                        </span>
                      )}
                      {req.approver_email && (
                        <a href={`mailto:${req.approver_email}`} style={{ fontSize: 12, color: "var(--muted)" }}>
                          ({req.approver_email})
                        </a>
                      )}
                    </div>
                  } />
                )}
                <InfoRow label="Reviewed At" value={fmt(req.reviewed_at)} />
                <div style={{ paddingTop: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13, color: "var(--muted)" }}>Use-Case Description</div>
                  <p style={{ fontSize: 14, lineHeight: 1.6 }}>{req.use_case}</p>
                </div>
                {req.verification_notes && (
                  <RiskNotes notes={req.verification_notes} riskLevel={req.risk_level} />
                )}
              </div>
            </div>

            {/* Assignments history */}
            <div className="card">
              <div className="card-header">Model Assignments</div>
              {assignments.length === 0 ? (
                <div className="empty">No model assigned yet.</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>Model</th><th>Provider</th><th>Assigned By</th><th>Calls</th><th>Tokens</th><th>Active</th></tr>
                    </thead>
                    <tbody>
                      {assignments.map((a) => (
                        <tr key={a.id}>
                          <td><strong>{a.model.name}</strong></td>
                          <td className="text-muted">{a.model.provider}</td>
                          <td className="text-muted">{a.assigned_by}</td>
                          <td>{a.total_calls}</td>
                          <td>{a.total_tokens}</td>
                          <td>{a.is_active ? "✅" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* ── Right: actions ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Active assignment + API key */}
            {activeAssignment && (
              <div className="card">
                <div className="card-header">Active Model</div>
                <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{activeAssignment.model.name}</div>
                    <div style={{ fontSize: 13, color: "var(--muted)" }}>{activeAssignment.model.provider}</div>
                    {activeAssignment.assignment_reason && (
                      <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 6, lineHeight: 1.5 }}>
                        {activeAssignment.assignment_reason}
                      </p>
                    )}
                  </div>
                  {activeAssignment.api_key && (
                    <ApiKeyCard apiKey={activeAssignment.api_key} />
                  )}
                </div>
              </div>
            )}

            {/* Review panel */}
            {canReview && (
              <div className="card">
                <div className="card-header">Review Request</div>
                <div className="card-body">
                  <form onSubmit={handleReview}>
                    <div className="form-group">
                      <label>Decision</label>
                      <select value={review.status} onChange={(e) => setReview((r) => ({ ...r, status: e.target.value }))}>
                        <option value="approved">Approve</option>
                        <option value="rejected">Reject</option>
                      </select>
                    </div>

                    {review.status === "approved" && models.length > 0 && (
                      <div className="form-group">
                        <label>
                          Assign Model
                          {req.preferred_model_id && (
                            <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 12, marginLeft: 6 }}>
                              (requester preferred: {models.find((m) => m.id === req.preferred_model_id)?.name ?? "—"})
                            </span>
                          )}
                        </label>
                        <select
                          value={review.model_id}
                          onChange={(e) => setReview((r) => ({ ...r, model_id: e.target.value }))}
                        >
                          <option value="">Auto-select best model</option>
                          {models.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name} — {m.provider} (tier: {m.tier}, max risk: {m.max_risk_level})
                            </option>
                          ))}
                        </select>
                        <div className="form-hint">Override the auto-selected model if needed.</div>
                      </div>
                    )}

                    <div className="form-group">
                      <label>Reviewer</label>
                      <input
                        value={review.reviewer || (auth?.email ?? "")}
                        onChange={(e) => setReview((r) => ({ ...r, reviewer: e.target.value }))}
                        readOnly={!isAdmin}
                        style={!isAdmin ? { background: "var(--bg)", color: "var(--muted)", cursor: "default" } : {}}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Notes</label>
                      <textarea value={review.verification_notes}
                        onChange={(e) => setReview((r) => ({ ...r, verification_notes: e.target.value }))}
                        placeholder="Optional review notes / rejection reason…"
                        style={{ minHeight: 70 }} />
                    </div>

                    {error && <div className="error-msg">{error}</div>}
                    <button
                      className={`btn ${review.status === "approved" ? "btn-success" : "btn-danger"}`}
                      type="submit" disabled={reviewing} style={{ width: "100%" }}
                    >
                      {reviewing ? "Saving…" : review.status === "approved"
                        ? "✅ Approve & Send API Key"
                        : "❌ Reject Request"}
                    </button>

                    {review.status === "approved" && (
                      <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 8, textAlign: "center" }}>
                        An API key will be generated and emailed to {req.requester_email}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            )}

            {/* Manual re-assign (post-approval, admin or manager of this request) */}
            {canApprove && req.status === "approved" && models.length > 0 && (
              <div className="card">
                <div className="card-header">Reassign Model</div>
                <div className="card-body">
                  <form onSubmit={handleAssign}>
                    <div className="form-group">
                      <label>Model</label>
                      <select value={manualModel} onChange={(e) => setManualModel(e.target.value)}>
                        {models.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name} ({m.provider}) — tier: {m.tier}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Reason</label>
                      <input value={manualReason} onChange={(e) => setManualReason(e.target.value)}
                        placeholder="Why reassigning?" />
                    </div>
                    {error && <div className="error-msg">{error}</div>}
                    <button className="btn btn-primary" style={{ width: "100%" }}>
                      Reassign & Regenerate Key
                    </button>
                    <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 8, textAlign: "center" }}>
                      A new API key will be generated and emailed to {req.requester_email}
                    </p>
                  </form>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}
